import React, { useState } from 'react';
import Badge, { getStatusColors } from '../ui/Badge';
import { formatShortDate, daysBetween } from '../../utils/date-utils';
import { Wrench, Truck, Package, Hammer, MapPin, StickyNote, Clock, ChevronDown, ArrowRight } from 'lucide-react';

const WALNUT = '#8B4513';

export default function JobsTableRow({ job, onJobClick }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getIcon = () => {
    if (job.isRepair) return { Icon: Wrench, color: 'var(--tag-orange-text)' };
    if (job.isDeliveryOnly) return { Icon: Truck, color: 'var(--tag-green-text)' };
    if (job.isPickupAtShop) return { Icon: Package, color: 'var(--tag-blue-text)' };
    return { Icon: Hammer, color: 'var(--tag-blue-text)' };
  };

  const getAgeColor = () => {
    const days = daysBetween(job.soldDate, new Date());
    if (days <= 7) return 'var(--status-text-bold-green)';
    if (days <= 21) return 'var(--tag-orange-text)';
    return 'var(--status-text-bold-red)';
  };

  const icon = getIcon();
  const ageColor = getAgeColor();
  const ageDays = daysBetween(job.soldDate, new Date());
  // No cutlistJobId = nothing TWP-built on this job — it's supplier-ordered
  // windows/doors only. Dashed (vs solid) right accent flags that; the color
  // itself tracks the job's stage, matching its status pill and JobsCardView.
  const isOrdered = !job.cutlistJobId;
  const sc = getStatusColors(job.status || 'sold');

  return (
    <div style={{
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      borderRight: `3px ${isOrdered ? 'dashed' : 'solid'} ${sc.color}`
    }}>
      {/* Main Row */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        {/* Icon */}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          background: `${icon.color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <icon.Icon size={19} strokeWidth={2} color={icon.color} />
        </div>

        {/* Job Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '4px'
          }}>
            {job.customerName || job.name}
          </div>

          {/* Contract Number */}
          {job.contractNumber && (
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'var(--brand-brown)',
              marginBottom: '4px'
            }}>
              #{job.contractNumber}
            </div>
          )}

          <div style={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginBottom: '4px'
          }}>
            {job.jobType || job.status?.replace(/_/g, ' ')}
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            fontSize: '11px',
            color: 'var(--text-tertiary)'
          }}>
            <span>Sold: {formatShortDate(job.soldDate)}</span>
            {job.scheduledDate && (
              <span>Install: {formatShortDate(job.scheduledDate)}</span>
            )}
          </div>

          {/* Age indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '4px'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: ageColor
            }} />
            <span style={{
              fontSize: '11px',
              color: ageColor,
              fontWeight: '600'
            }}>
              Age: {ageDays}d
            </span>
          </div>
        </div>

        {/* Status & Chevron */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0
        }}>
          <Badge status={job.status || 'sold'}>
            {job.status || 'sold'}
          </Badge>

          <span style={{
            display: 'flex',
            color: 'var(--text-tertiary)',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            <ChevronDown size={16} strokeWidth={2} />
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{
          padding: '12px 16px 16px 72px',
          background: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          {/* Address */}
          {job.address && (
            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary)',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <MapPin size={12} strokeWidth={2} />
              <span>{job.address}</span>
            </div>
          )}

          {/* Notes */}
          {job.notes && (
            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary)',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px'
            }}>
              <StickyNote size={12} strokeWidth={2} style={{ marginTop: '2px', flexShrink: 0 }} />
              <span>{job.notes}</span>
            </div>
          )}

          {/* Job Details */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            marginBottom: '8px'
          }}>
            {job.windowCount > 0 && <span>{job.windowCount} windows</span>}
            {job.doorCount > 0 && <span>{job.doorCount} doors</span>}
            {job.durationDays && <span>{job.durationDays} days</span>}
            {job.assignedCrew && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Hammer size={11} strokeWidth={2} /> {job.assignedCrew}</span>}
          </div>

          {/* Last Update */}
          {job.statusHistory && job.statusHistory.length > 0 && (
            <div style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Clock size={11} strokeWidth={2} />
              <span>
                Last updated: {new Date(job.statusHistory[job.statusHistory.length - 1].timestamp).toLocaleDateString()} {new Date(job.statusHistory[job.statusHistory.length - 1].timestamp).toLocaleTimeString()}
              </span>
            </div>
          )}

          {/* Open Details Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onJobClick(job);
            }}
            style={{
              padding: '8px 12px',
              background: `${WALNUT}14`,
              border: `1px solid ${WALNUT}`,
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              color: WALNUT,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>Open Job Details</span>
            <ArrowRight size={13} strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
}